// import axios from 'axios';


export default {
  namespaced: true,
  
  state: {
    kycRecords: [],
    auditLogs: [],
    loading: false,
    error: null,
    statusFilter: 'all',
    searchQuery: '',
  },
  
  getters: {
    filteredKYCs: (state) => {
      let filtered = [...state.kycRecords];
      
      // Status filtering
      if (state.statusFilter !== 'all') {
        filtered = filtered.filter(kyc => kyc.status === state.statusFilter);
      }
      
      // Search filtering
      if (state.searchQuery) {
        const query = state.searchQuery.toLowerCase();
        filtered = filtered.filter(kyc => 
          kyc.id.toLowerCase().includes(query) ||
          kyc.customerName.toLowerCase().includes(query) ||
          kyc.agent.toLowerCase().includes(query)
        );
      }
      
      return filtered;
    },
    
    getKycById: (state) => (id) => {
      return state.kycRecords.find(kyc => kyc.id === id);
    },
    
    getAuditLogsForKyc: (state) => (kycId) => {
      return state.auditLogs.filter(log => log.kycId === kycId);
    }
  },
  
  mutations: {
    setKycRecords(state, records) {
      state.kycRecords = records;
    },
    
    setAuditLogs(state, logs) {
      state.auditLogs = logs;
    },
    
    setLoading(state, isLoading) {
      state.loading = isLoading;
    },
    
    setError(state, error) {
      state.error = error;
    },
    
    setStatusFilter(state, filter) {
      state.statusFilter = filter;
    },
    
    setSearchQuery(state, query) {
      state.searchQuery = query;
    },
    
    updateKycStatus(state, { id, status, reviewedBy }) {
      const kyc = state.kycRecords.find(k => k.id === id);
      if (kyc) {
        kyc.status = status;
        kyc.reviewedBy = reviewedBy;
      }
    },
    
    addAuditLog(state, log) {
      state.auditLogs.unshift(log);
    }
  },
  
  actions: {
    async fetchKycRecords({ commit }) {
      commit('setLoading', true);
      try {
        const mockData = [
          {
            id: 'KYC-1001',
            date: '2023-10-01',
            customerName: 'John Doe',
            agent: 'Agent Smith',
            company: 'Bank A',
            status: 'pending'
          },
          {
            id: 'KYC-1002',
            date: '2023-10-02',
            customerName: 'Jane Smith',
            agent: 'Agent Brown',
            company: 'Bank B',
            status: 'approved'
          },
          {
            id: 'KYC-1003',
            date: '2023-10-03',
            customerName: 'Alice Johnson',
            agent: 'Agent White',
            company: 'Bank C',
            status: 'declined'
          },
          {
            id: 'KYC-1004',
            date: '2023-10-04',
            customerName: 'Bob Williams',
            agent: 'Agent Green',
            company: 'Bank A',
            status: 'pending'
          },
          {
            id: 'KYC-1005',
            date: '2023-10-05',
            customerName: 'Charlie Brown',
            agent: 'Agent Black',
            company: 'Bank B',
            status: 'approved'
          }
        ];
        
        commit('setKycRecords', mockData);
        commit('setError', null);
      } catch (error) {
        console.error('Error fetching KYC records:', error);
        errorHandler.tomcatError(error)
      } finally {
        commit('setLoading', false);
      }
    },
    
    async fetchAuditLogs({ commit }) {
      commit('setLoading', true);
      try {
        // In a real app, you would fetch from an API
        // const response = await axios.get(`${apiBaseUrl}/kyc/audit-logs`);
        
        // For now, use mock data
        const mockLogs = [
          {
            timestamp: '2023-10-01T10:15:30',
            action: 'Approved',
            performedBy: 'Admin1',
            kycId: 'KYC-1002',
            details: 'All documents verified'
          },
          {
            timestamp: '2023-10-03T14:20:45',
            action: 'Declined',
            performedBy: 'Admin2',
            kycId: 'KYC-1003',
            details: 'Incomplete documentation'
          }
        ];
        
        commit('setAuditLogs', mockLogs);
        commit('setError', null);
      } catch (error) {
        console.error('Error fetching audit logs:', error);
        errorHandler.tomcatError(error)
      } finally {
        commit('setLoading', false);
      }
    },
    
    async approveKyc({ commit, rootState }, kycId) {
      commit('setLoading', true);
      try {
        // In a real app, you would call an API
        // await axios.post(`${apiBaseUrl}/kyc/${kycId}/approve`);
        
        const currentUser = rootState.user ? rootState.user.username : 'Admin';
        
        // Update local state
        commit('updateKycStatus', { 
          id: kycId, 
          status: 'approved',
          reviewedBy: currentUser
        });
        
        // Add audit log
        commit('addAuditLog', {
          timestamp: new Date().toISOString(),
          action: 'Approved',
          performedBy: currentUser,
          kycId: kycId,
          details: 'KYC application approved'
        });
        
        return true;
      } catch (error) {
        console.error('Error approving KYC:', error);
        errorHandler.tomcatError(error)
        return false;
      } finally {
        commit('setLoading', false);
      }
    },
    
    async declineKyc({ commit, rootState }, { kycId, reason }) {
      commit('setLoading', true);
      try {
        // In a real app, you would call an API
        // await axios.post(`${apiBaseUrl}/kyc/${kycId}/decline`, { reason });
        
        const currentUser = rootState.user ? rootState.user.username : 'Admin';
        
        // Update local state
        commit('updateKycStatus', { 
          id: kycId, 
          status: 'declined',
          reviewedBy: currentUser
        });
        
        // Add audit log
        commit('addAuditLog', {
          timestamp: new Date().toISOString(),
          action: 'Declined',
          performedBy: currentUser,
          kycId: kycId,
          details: `KYC application declined: ${reason}`
        });
        
        return true;
      } catch (error) {
        console.error('Error declining KYC:', error);
        errorHandler.tomcatError(error)
        return false;
      } finally {
        commit('setLoading', false);
      }
    },
    
    updateFilters({ commit }, { statusFilter, searchQuery }) {
      if (statusFilter !== undefined) {
        commit('setStatusFilter', statusFilter);
      }
      
      if (searchQuery !== undefined) {
        commit('setSearchQuery', searchQuery);
      }
    },
    
    async exportReport({ state }) {
      try {
        // In a real app, you might call an API to generate a report
        // const response = await axios.get(`${apiBaseUrl}/kyc/export`, { responseType: 'blob' });

        // For now, just log that we're exporting
        console.log('Exporting KYC report with filters:', {
          statusFilter: state.statusFilter,
          searchQuery: state.searchQuery
        });

        // In a real app, you would handle the file download
        // const url = window.URL.createObjectURL(new Blob([response.data]));
        // const link = document.createElement('a');
        // link.href = url;
        // link.setAttribute('download', 'kyc-report.csv');
        // document.body.appendChild(link);
        // link.click();

        return true;
      } catch (error) {
        console.error('Error exporting report:', error);
        errorHandler.tomcatError(error)
        return false;
      }
    },

    downloadZip: async (context, id) => {
      try {
        const url = `${bankApi}signup/signupData/download-pdf?id=${encodeURIComponent(id)}`;
        window.open(url, '_blank');
        return true;
      } catch (e) {
        console.error('Error downloadZip:', e);
        errorHandler.tomcatError(e);
        return false;
      }
    },

    async downloadKYC({ state }) {
      try {
        // In a real app, you might call an API to generate a report
        // const response = await axios.get(`${apiBaseUrl}/kyc/export`, { responseType: 'blob' });

        // For now, just log that we're exporting
        console.log('Exporting KYC report with filters:', {
          statusFilter: state.statusFilter,
          searchQuery: state.searchQuery
        });

        // In a real app, you would handle the file download
        // const url = window.URL.createObjectURL(new Blob([response.data]));
        // const link = document.createElement('a');
        // link.href = url;
        // link.setAttribute('download', 'kyc-report.csv');
        // document.body.appendChild(link);
        // link.click();

        return true;
      } catch (error) {
        console.error('Error exporting report:', error);
        errorHandler.tomcatError(error)
        return false;
      }
    },
  }
}; 